import {useContext, useEffect, useMemo, useRef, useState} from "react";
import axios from "axios";
import {loadStripe} from "@stripe/stripe-js/pure";
import {CardElement, Elements, useElements, useStripe} from "@stripe/react-stripe-js";
import Checkbox from "@autocx/forms/src/controls/checkbox";
import Currency from "@autocx/forms/src/controls/currency";
import {WebsiteContext} from "../../../index";
import Spinner from "../../../components/spinner";
import WebsiteCardElement from "../../../components/website-card-element";
import ModalContentTransition from "../../../components/modal-content-transition";
import SavedPaymentMethodSummary from "../../../components/saved-payment-method-summary";
import {
    formatPaymentMethodBrand,
    getCustomerPaymentErrorMessage,
    getPaymentConfirmationOptions
} from "../../../components/website-payment-field";

const createIdempotencyKey = () => (
    typeof window !== "undefined" && window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random()}`
);

const getRequestError = (error, fallback) => {
    const response = error?.response?.data;
    if (typeof response === "string" && response.trim()) return response;
    if (typeof response?.message === "string" && response.message.trim()) return response.message;
    return error?.message || fallback;
};

const formatCoverageDeadline = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return new Intl.DateTimeFormat(undefined, {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit"
    }).format(date);
};

function HoldAuthorisationForm({lotId, recovery, onReadyToRetry, onBack}) {
    const context = useContext(WebsiteContext);
    const stripe = useStripe();
    const elements = useElements();
    const methods = recovery?.savedPaymentMethods || [];
    const [paymentMethodId, setPaymentMethodId] = useState(methods[0]?.id || null);
    const [cardComplete, setCardComplete] = useState(false);
    const [cardError, setCardError] = useState("");
    const [consentAccepted, setConsentAccepted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const idempotencyKey = useRef(createIdempotencyKey());
    const usesSavedCard = methods.length > 0;
    const coverageDeadline = formatCoverageDeadline(recovery?.requiredUntil);
    const isReplacement = recovery?.status === "replacement_required";
    const consentError = !usesSavedCard && error === "Please confirm that this card can be saved.";

    const selectPaymentMethod = (methodId) => {
        if (submitting || methodId === paymentMethodId) return;
        // A different card is a different authorisation request. Unchanged
        // retries retain their key so an uncertain response cannot duplicate a hold.
        idempotencyKey.current = createIdempotencyKey();
        setPaymentMethodId(methodId);
        setError("");
    };

    const authorise = async (event) => {
        event.preventDefault();
        if (submitting) return;
        setError("");
        setCardError("");

        if (!stripe || !elements) {
            setError("Secure card entry is still loading. Please try again.");
            return;
        }
        if (usesSavedCard && !paymentMethodId) {
            setError("Choose a saved card to continue.");
            return;
        }
        if (!usesSavedCard && (!cardComplete || !consentAccepted)) {
            if (!cardComplete) setCardError("Enter your card details to continue.");
            else setError("Please confirm that this card can be saved.");
            return;
        }

        const card = usesSavedCard ? null : elements.getElement(CardElement);
        if (!usesSavedCard && !card) {
            setError("Secure card entry is unavailable. Please try again.");
            return;
        }

        setSubmitting(true);
        try {
            const {data: operation} = await axios.post(`/api/v2/salesevents/lots/${lotId}/bid-hold`, {
                idempotencyKey: idempotencyKey.current,
                paymentMethodId: usesSavedCard ? paymentMethodId : null,
                consentAccepted: !usesSavedCard && consentAccepted,
                consentVersion: usesSavedCard ? null : recovery.consentVersion
            });

            if (operation?.status === "covered" || operation?.status === "succeeded") {
                await onReadyToRetry();
                return;
            }
            if (!operation?.clientSecret || !operation?.operationId)
                throw new Error("The card hold could not be started.");

            const confirmation = await stripe.confirmCardPayment(
                operation.clientSecret,
                getPaymentConfirmationOptions(card, usesSavedCard, operation.status === "requires_action")
            );
            if (confirmation?.error) throw confirmation.error;
            if (!confirmation?.paymentIntent?.id)
                throw new Error("The card hold could not be confirmed.");

            const {data: completed} = await axios.post(
                `/api/v2/salesevents/lots/${lotId}/bid-hold/${operation.operationId}/complete`,
                {}
            );
            if (completed?.status !== "succeeded")
                throw new Error("The card hold is still being confirmed. Please try again.");
            await onReadyToRetry();
        } catch (authorisationError) {
            setError(authorisationError?.type || authorisationError?.code
                ? getCustomerPaymentErrorMessage(authorisationError)
                : getRequestError(
                    authorisationError,
                    "The card hold could not be authorised. Check the details or try another card."
                ));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form className="mt-4 w-full text-left space-y-4" onSubmit={authorise} noValidate>
            {isReplacement ? (
                <div className="rounded-theme bg-gray-50 px-4 py-3 text-sm leading-relaxed text-gray-700">
                    <div className="font-semibold text-gray-900">Why another hold is needed</div>
                    <p className="mb-0 mt-0.5">
                        Your current hold does not cover the amount or time required for this auction.
                        {" "}Authorise a new hold to place your bid.
                    </p>
                </div>
            ) : null}
            <div
                className="border-b border-gray-200 pb-4 text-center"
                data-testid="bid-hold-summary"
            >
                <div
                    className="text-xl font-semibold text-gray-900"
                    data-testid="bid-hold-summary-title"
                >
                    <Currency.DisplayValue
                        currency={recovery.currency || context.currency}
                        country={context.country}
                        value={recovery.requiredAmount}
                        emptyPlaceholder=""
                    />
                    <span>{isReplacement ? " new temporary card hold" : " temporary card hold"}</span>
                </div>
                <div
                    className="mt-1 text-base text-gray-700"
                    data-testid="bid-hold-summary-note"
                >
                    {recovery.coverageNote}
                </div>
                {coverageDeadline ? (
                    <div
                        className="mt-0.5 text-sm text-gray-500"
                        data-testid="bid-hold-summary-expiry"
                    >
                        Hold required until {coverageDeadline}
                    </div>
                ) : null}
            </div>

            {usesSavedCard ? (
                <div>
                    <div className="text-sm font-semibold text-gray-800 mb-2">
                        {methods.length === 1 ? "Card" : "Choose a card"}
                    </div>
                    <div className="space-y-2 p-px">
                        {methods.map((method) => {
                            const selected = paymentMethodId === method.id;
                            return (
                                <label key={method.id}
                                       className={`flex items-center gap-3 rounded-theme border bg-white px-3 py-3 cursor-pointer transition-colors ${
                                           methods.length > 1 && selected
                                               ? "border-primary-500 ring-1 ring-inset ring-primary-500"
                                               : "border-gray-200 hover:border-gray-300"
                                       }`}>
                                    <SavedPaymentMethodSummary method={method} className="grow"/>
                                    {methods.length > 1 ? (
                                        <input
                                            type="radio"
                                            name="bid-hold-card"
                                            value={method.id}
                                            checked={selected}
                                            onChange={() => selectPaymentMethod(method.id)}
                                            disabled={submitting}
                                            aria-label={`${formatPaymentMethodBrand(method.brand)} ending in ${method.lastFour || "unknown"}`}
                                        />
                                    ) : null}
                                </label>
                            );
                        })}
                    </div>
                    <a className="text-link text-sm font-semibold inline-block mt-2" href="/my-account/payments">
                        Manage Payment Methods
                    </a>
                </div>
            ) : (
                <>
                    <WebsiteCardElement
                        id="bid_hold_card"
                        label="Card Details"
                        required
                        error={cardError}
                        errorClassName="animate__animated animate__headShake"
                        options={{hidePostalCode: true, disableLink: true}}
                        onChange={(cardEvent) => {
                            setCardComplete(cardEvent.complete === true);
                            if (cardEvent.error) setCardError(getCustomerPaymentErrorMessage(cardEvent.error));
                            else if (cardError) setCardError("");
                        }}
                    />
                    <Checkbox
                        id="bid_hold_card_consent"
                        type="checkbox"
                        name="bid_hold_card_consent"
                        label={recovery.consentText}
                        required
                        error={consentError}
                        ariaDescribedBy={consentError ? "bid_hold_card_consent_error" : undefined}
                        value={consentAccepted}
                        onChange={(_event, _name, checked) => {
                            setConsentAccepted(checked);
                            if (checked && consentError) setError("");
                        }}
                    />
                </>
            )}

            {recovery.captureConsentText ? (
                <p className="text-sm leading-relaxed text-gray-700 mb-0">
                    {recovery.captureConsentText}
                </p>
            ) : null}
            {error ? (
                <p
                    id={consentError ? "bid_hold_card_consent_error" : undefined}
                    role="alert"
                    className="text-sm font-semibold text-red-600 mb-0"
                >
                    {error}
                </p>
            ) : null}
            <div className="space-y-2 pt-1">
                <button type="submit" className="button w-full justify-center relative" disabled={submitting}>
                    <span className={submitting ? "opacity-0" : "opacity-100"}>
                        {isReplacement ? "Authorise New Hold & Place Bid" : "Authorise Hold & Place Bid"}
                    </span>
                    {submitting ? (
                        <Spinner containerClassName="absolute inset-0 my-0" className="h-5 w-5"/>
                    ) : null}
                </button>
                <button type="button" className="text-link text-sm font-semibold w-full" onClick={onBack} disabled={submitting}>
                    Back to Bid
                </button>
            </div>
        </form>
    );
}

export default function BidHoldRecovery({lotId, onReadyToRetry, onBack}) {
    const [recovery, setRecovery] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;
        axios.get(`/api/v2/salesevents/lots/${lotId}/bid-hold`)
            .then(({data}) => {
                if (cancelled) return;
                if (data?.status === "covered" || data?.status === "not_required") {
                    onReadyToRetry();
                    return;
                }
                if (data?.status === "review_required") {
                    setError("An earlier auction on your account still needs to be resolved before you can bid again. No new hold has been placed. Please contact the auction team.");
                    return;
                }
                setRecovery(data);
            })
            .catch((requestError) => {
                if (!cancelled) setError(getRequestError(
                    requestError,
                    "The card hold details could not be loaded. Please try again."
                ));
            });
        return () => { cancelled = true; };
    }, [lotId]);

    const stripePromise = useMemo(() => {
        if (!recovery?.stripePublishableKey || !recovery?.stripeAccountId) return null;
        return loadStripe(recovery.stripePublishableKey, {stripeAccount: recovery.stripeAccountId});
    }, [recovery]);

    const recoveryStep = error ? "error" : !recovery || !stripePromise ? "loading" : "ready";

    return (
        <ModalContentTransition transitionKey={recoveryStep}>
            {error ? (
            <div className="mt-4 w-full">
                <p role="alert" className="text-sm font-semibold text-red-600">{error}</p>
                <button type="button" className="text-link text-sm font-semibold" onClick={onBack}>Back to Bid</button>
            </div>
            ) : !recovery || !stripePromise ? (
                <div className="flex min-h-[15rem] w-full items-center justify-center">
                    <Spinner containerClassName="my-0" className="h-6 w-6"/>
                </div>
            ) : (
                <Elements stripe={stripePromise}>
                    <HoldAuthorisationForm
                        lotId={lotId}
                        recovery={recovery}
                        onReadyToRetry={onReadyToRetry}
                        onBack={onBack}
                    />
                </Elements>
            )}
        </ModalContentTransition>
    );
}
